import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
const GTM_ID = import.meta.env.VITE_GTM_CONTAINER_ID as string | undefined;
const TIKTOK_PIXEL = import.meta.env.VITE_TIKTOK_PIXEL_ID as string | undefined;
const META_PIXEL = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    ttq?: { track: (e: string, p?: Record<string, unknown>) => void; page: () => void; load: (id: string) => void };
    fbq?: (...args: unknown[]) => void;
  }
}

let injected = false;
function injectScripts() {
  if (injected || typeof window === "undefined") return;
  const consent = localStorage.getItem("pluggz.cookie-consent");
  if (consent !== "accepted") return;
  injected = true;

  if (GA4_ID) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer!.push(arguments as unknown as unknown[]); };
    window.gtag("js", new Date());
    window.gtag("config", GA4_ID, { send_page_view: false });
  }

  if (GTM_ID) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(s);
  }

  if (META_PIXEL) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (function (f: any, b: Document, e: string, v: string) {
      if (f.fbq) return;
      const n: any = (f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); });
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
      const t = b.createElement(e) as HTMLScriptElement; t.async = true; t.src = v;
      const s = b.getElementsByTagName(e)[0]; s.parentNode!.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    window.fbq!("init", META_PIXEL);
  }

  if (TIKTOK_PIXEL) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (function (w: any, d: Document, t: string) {
      w.TiktokAnalyticsObject = t;
      const ttq: any = (w[t] = w[t] || []);
      ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
      ttq.setAndDefer = function (e: any, n: string) { e[n] = function () { e.push([n].concat(Array.prototype.slice.call(arguments, 0))); }; };
      for (const m of ttq.methods) ttq.setAndDefer(ttq, m);
      ttq.load = function (e: string) {
        const s = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = s; ttq._t = ttq._t || {}; ttq._t[e] = +new Date(); ttq._o = ttq._o || {}; ttq._o[e] = {};
        const o = d.createElement("script") as HTMLScriptElement; o.type = "text/javascript"; o.async = true; o.src = `${s}?sdkid=${e}&lib=${t}`;
        const a = d.getElementsByTagName("script")[0]; a.parentNode!.insertBefore(o, a);
      };
      ttq.load(TIKTOK_PIXEL!);
      ttq.page();
    })(window, document, "ttq");
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("pluggz.consent-accepted", injectScripts, { once: true });
}

export function AnalyticsProvider() {
  const router = useRouter();
  useEffect(() => {
    injectScripts();
    const unsub = router.subscribe("onResolved", ({ toLocation }) => {
      const path = toLocation.pathname + (toLocation.searchStr ?? "");
      if (window.gtag && GA4_ID) window.gtag("event", "page_view", { page_path: path });
      if (window.fbq) window.fbq("track", "PageView");
      if (window.ttq) window.ttq.page();
    });
    return () => { unsub(); };
  }, [router]);
  return null;
}

export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  if (window.gtag) window.gtag("event", name, params);
  if (window.fbq) window.fbq("trackCustom", name, params);
  if (window.ttq) window.ttq.track(name, params);
  if (window.dataLayer) window.dataLayer.push({ event: name, ...params });
}